import React, { useRef, useEffect } from 'react';
import { useField } from 'formik';
import ErrorMessage from 'components/ErrorMessage';
import Tooltip from 'components/Tooltip';
import { EditorContainer, Toolbar, ToolbarButton, EditorContent, HeaderRow, LinkInputPopover } from './styles';
import { MdFormatBold, MdFormatItalic, MdFormatUnderlined, MdInsertLink, MdLinkOff } from 'react-icons/md';

interface RichTextEditorProps {
  name: string;
  title: string;
  isRequired?: boolean;
  hasQuestionMark?: boolean;
  questionMarkText?: string;
  placeholder?: string;
  maxLength?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  name,
  title,
  isRequired,
  hasQuestionMark,
  questionMarkText,
  placeholder,
  maxLength = 5000,
}) => {
  const [field, meta, helpers] = useField(name);
  const editorRef = useRef<HTMLDivElement>(null);
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const savedSelectionRef = useRef<Range | null>(null);

  // Sync initial / external value to contentEditable
  useEffect(() => {
    if (editorRef.current && field.value !== editorRef.current.innerHTML) {
      // Avoid replacing if it matches text representation
      if ((field.value || '') !== (editorRef.current.innerHTML || '')) {
        editorRef.current.innerHTML = field.value || '';
      }
    }
  }, [field.value]);

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      // If it's just empty breaks, consider empty
      const isBlank = html === '<br>' || html.trim() === '';
      helpers.setValue(isBlank ? '' : html);
    }
  };

  const executeCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    handleInput();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (sel && savedSelectionRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  const handleOpenLinkModal = () => {
    saveSelection();
    setLinkUrl('');
    setShowLinkInput(prev => !prev);
  };

  const applyLink = (e?: React.SyntheticEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!linkUrl) return;

    let formattedUrl = linkUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl) && !formattedUrl.startsWith('/')) {
      formattedUrl = `https://${formattedUrl}`;
    }

    if (editorRef.current) {
      editorRef.current.focus();
    }
    restoreSelection();

    const sel = window.getSelection();
    const isCollapsed = !sel || sel.isCollapsed || sel.toString().trim() === '';

    if (isCollapsed) {
      const a = document.createElement('a');
      a.href = formattedUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = formattedUrl;

      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(a);
        range.setStartAfter(a);
        range.setEndAfter(a);
        sel.removeAllRanges();
        sel.addRange(range);
      } else if (editorRef.current) {
        editorRef.current.appendChild(a);
      }
    } else {
      executeCommand('createLink', formattedUrl);
      if (editorRef.current) {
        const links = editorRef.current.getElementsByTagName('a');
        for (let i = 0; i < links.length; i++) {
          if (links[i].href === formattedUrl || links[i].getAttribute('href') === formattedUrl) {
            links[i].setAttribute('target', '_blank');
            links[i].setAttribute('rel', 'noopener noreferrer');
          }
        }
      }
    }

    handleInput();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const removeLink = () => {
    executeCommand('unlink');
    setShowLinkInput(false);
  };

  const textLength = editorRef.current
    ? (editorRef.current.innerText || '').length
    : field.value
    ? field.value.replace(/<[^>]*>/g, '').length
    : 0;
  const color = textLength >= maxLength ? '#ec3237' : '#9a9a9a';

  return (
    <EditorContainer>
      <HeaderRow>
        <label>
          <p>{title}</p>
          <small style={{ color, marginLeft: '8px' }}>{`(${textLength}/${maxLength})`}</small>
          {isRequired && <span style={{ color: '#f00', marginLeft: '5px' }}>*</span>}
          {hasQuestionMark && <Tooltip text={questionMarkText} />}
        </label>
      </HeaderRow>

      <div className="editor-wrapper">
        <Toolbar>
          <ToolbarButton
            type="button"
            title="Negrito (Ctrl+B)"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              executeCommand('bold');
            }}>
            <MdFormatBold size={20} />
          </ToolbarButton>

          <ToolbarButton
            type="button"
            title="Itálico (Ctrl+I)"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              executeCommand('italic');
            }}>
            <MdFormatItalic size={20} />
          </ToolbarButton>

          <ToolbarButton
            type="button"
            title="Sublinhado (Ctrl+U)"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              executeCommand('underline');
            }}>
            <MdFormatUnderlined size={20} />
          </ToolbarButton>

          <div className="divider" />

          <ToolbarButton
            type="button"
            title="Inserir Hiperlink"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              handleOpenLinkModal();
            }}>
            <MdInsertLink size={20} />
          </ToolbarButton>

          <ToolbarButton
            type="button"
            title="Remover Hiperlink"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              removeLink();
            }}>
            <MdLinkOff size={20} />
          </ToolbarButton>

          {showLinkInput && (
            <LinkInputPopover>
              <input
                type="text"
                placeholder="https://exemplo.com"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    applyLink(e);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowLinkInput(false);
                  }
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  applyLink(e);
                }}>
                Inserir
              </button>
              <button
                type="button"
                className="cancel"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLinkInput(false);
                }}>
                Cancelar
              </button>
            </LinkInputPopover>
          )}
        </Toolbar>

        <EditorContent
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          data-placeholder={placeholder || 'Digite a descrição aqui...'}
          className="rich-editor-content"
        />
      </div>

      <ErrorMessage name={name} />
    </EditorContainer>
  );
};

export default RichTextEditor;
